import { ServiceUpdateResponseDataData } from '@kaa/api/providers/model/serviceUpdateResponseDataData';
import { httpTo } from '@kaa/api/providers/utilities';
import { ActionType, useActionDispatch } from '@kaa/common/context';
import { useAsyncCallback } from '@kaa/common/utils';
import { maxLength } from '@kaa/common/validation';
import { i18nKeys } from '@kaa/i18n/providers/keys';
import {
  AlertType,
  getAlertPropsByType,
  getAlertTypeByServiceStatus,
  SwActionGroup,
  SwAlert,
  SwButton,
  SwContainer,
  SwFetchErrorMessage,
  SwForm,
  SwFormSubmitMessage,
  SwModalRenderProps,
  SwTitle,
} from '@kaa/ui-flanders/components';
import { Formik, FormikActions } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, useSelectedProviderIdState } from '../../../../utils';
import {
  EventAction,
  EventCategory,
  EventLabel,
  sendCustomInteractionToGTM,
} from '../../../../utils/google-analytics';
import {
  createValidatorWithServerErrorHandled,
  handleApiError,
} from '../../../../utils/validation';
import { PrestationForm } from '../form/PrestationForm';
import { PrestationFormData } from '../form/PrestationForm.types';
import {
  getDefaultWorksValidation,
  getPrestationStatusAlert,
  getRequiredDefaultWorksFields,
} from '../form/PrestationForm.utils';
import { PrestationsModalParameters } from '../PrestationsModals.constants';
import { PrestationEditModalConfirmation } from './PrestationEditModalConfirmation';
import { PrestationsSearchTableRowStatusContent } from '../../search/components/PrestationsSearchTableRowStatusContent';
import {sanitizeComment} from "../../../../../common/utils/sanitizeComment";

type PrestationEditModalProps = {
  prestationId: string;
  searchedProviderId: number;
  parameters: PrestationsModalParameters;
  validateService: (parameters: PrestationsModalParameters) => void;
  onSuccess: (props: ServiceUpdateResponseDataData) => void;
};

export const PrestationEditModal = ({
  prestationId,
  searchedProviderId,
  parameters,
  validateService,
  setConfirmCloseModal,
  onSuccess,
}: SwModalRenderProps<PrestationEditModalProps>) => {
  const { t } = useTranslation();
  const { providers } = useApi();
  const providerId = useSelectedProviderIdState();

  const dispatchAction = useActionDispatch();

  const [
    { value: prestationData, loading, error },
    getPrestationData,
  ] = useAsyncCallback(
    async () => {
      const [
        {
          data: {
            data: { headquarter, subsidiaries },
          },
        },
        {
          data: { data },
        },
      ] = await Promise.all([
        providers.getProviderRelatives(providerId),
        providers.getService(searchedProviderId, prestationId),
      ]);

      return {
        relatives: [
          headquarter,
          ...subsidiaries.filter(({ isActive }) => isActive),
        ],
        prestation: data,
      };
    },
    [providers, providerId, prestationId],
    { loading: true },
  );

  const [{ loading: workersLoading }, getWorkers] = useAsyncCallback(
    async (query: string, searchedProviderId: string) => {
      const {
        data: { data },
      } = await providers.getWorkers(providerId, {
        workerNameOrNiss: query,
        searchedProviderId,
      });
      return data;
    },
    [providers],
  );

  const [{ loading: customersLoading }, getCustomers] = useAsyncCallback(
    async (query: string) => {
      const {
        data: { data },
      } = await providers.getCustomers({
        contractId: query,
      });
      return data;
    },
    [providers],
  );

  const [{ value: submitedForm }, submit] = useAsyncCallback(
    async (
      formikData: PrestationFormData,
      formikActions: FormikActions<PrestationFormData>,
    ) => {
      if(formikData.comment){
        formikData.comment = sanitizeComment(formikData.comment)
      }
      const [error, response] = await httpTo(
        providers.updateWorkerService(
          formikData.providerId,
          prestationId,
          formikData,
        ),
      );
      return { error, response: response?.data, formikData, formikActions };
    },
    [providers],
  );

  useEffect(() => {
    getPrestationData();
  }, [getPrestationData]);

  useEffect(() => {
    if (submitedForm) {
      const { error, response, formikData, formikActions } = submitedForm;
      const { setSubmitting, setStatus, resetForm } = formikActions;

      setSubmitting(false);

      if (handleApiError(error, formikActions)) {
        return;
      }

      if (!response) {
        setStatus({
          msg: t(i18nKeys.contactPersons.modal.add.errorMessage),
          type: AlertType.ERROR,
        });
        return;
      }

      const { data, actions } = response;

      if (actions) {
        dispatchAction({
          type: ActionType.ADD,
          payload: actions,
        });
      }

      const { service, services } = parameters;

      const { service: editedService, ...rest } = data;

      setConfirmCloseModal(false);
      resetForm(formikData);
      validateService({
        services,
        service: {
          ...service,
          ...editedService,
        },
      });

      onSuccess(rest);

      sendCustomInteractionToGTM(
        EventCategory.PRESTATIONS,
        EventAction.FORM_CONFIRMATION,
        EventLabel.PRESTATIONS_EDIT,
      );
    }
  }, [submitedForm]);

  if (loading && !prestationData) {
    return (
      <>
        <SwTitle tagName="h2">
          {t(i18nKeys.prestations.modals.edit.title)}
        </SwTitle>
        <SwContainer loading />
      </>
    );
  }
  if (error || !prestationData) {
    return (
      <SwContainer error>
        <SwFetchErrorMessage
          onClick={() => {
            getPrestationData();
          }}
        />
      </SwContainer>
    );
  }

  if (submitedForm?.response) {
    return (
      <>
        <SwTitle tagName="h2">
          {t(i18nKeys.prestations.modals.edit.title)}
        </SwTitle>
        <PrestationEditModalConfirmation />
      </>
    );
  }

  const { relatives, prestation } = prestationData;

  const validate = createValidatorWithServerErrorHandled({
    ...getDefaultWorksValidation(),
    comment: maxLength(255),
  });

  const status = getPrestationStatusAlert(prestation, t);
  const alertProps = getAlertPropsByType(
      getAlertTypeByServiceStatus(prestation.status)
  );

  return (
    <>
      <SwTitle tagName="h2">
        {t(i18nKeys.prestations.modals.edit.title)}
      </SwTitle>

      {status && (
        <SwAlert
          {...alertProps}
          title={t(i18nKeys.general.serviceStatus[prestation.status])}
          style={{backgroundColor: alertProps.modInfo ? '#e0e3f5' : ''}}
        >
          <PrestationsSearchTableRowStatusContent
            providerId={searchedProviderId}
            service={prestation}
            workerId={prestation.workerId}
          />
        </SwAlert>
      )}

      <Formik<Partial<PrestationFormData>>
        onSubmit={submit}
        validate={validate}
        initialValues={getRequiredDefaultWorksFields(prestation)}
        render={({ handleSubmit, isSubmitting, dirty, values }) => {
          return (
            <SwForm onSubmit={handleSubmit}>
              <PrestationForm
                getWorkers={getWorkers}
                getCustomers={getCustomers}
                relatives={relatives}
                modDisabled={{
                  providerId: true,
                  customerContractId: true,
                }}
                comments={prestation.comments}
                customerFullname={prestation.customerFullname}
                searchedProviderId={values.providerId}
                customersLoading={customersLoading}
                workersLoading={workersLoading}
              />
              <SwActionGroup style={{ marginTop: '3rem' }}>
                <SwButton
                  type="submit"
                  modLoading={isSubmitting}
                  modLarge
                  modDisabled={!dirty}
                >
                  {t(i18nKeys.prestations.modals.edit.cta)}
                </SwButton>
              </SwActionGroup>
              <SwFormSubmitMessage />
            </SwForm>
          );
        }}
      />
    </>
  );
};
